// POS appearance: the POS UI layout document (JSON, exactly as the POS
// reads it — a visual tile editor is a later slice), what the receipt
// prints, how new orders alert, and which release rail the terminals follow.

import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useBase } from '../../scope';
import { Err } from './controls';
import { Check, errMsg, Head, intOrNull, SaveBtn, useAction } from './shared';
import type { OrderAlertSettings, PosUpdateChannel, ReceiptSettings, SettingsBundle } from './types';

export function Pos() {
  const base = useBase();
  const [bundle, setBundle] = useState<SettingsBundle | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<SettingsBundle>(`${base}/settings`).then(setBundle, (e) => setLoadErr(errMsg(e)));
  }, [base]);

  return (
    <>
      <Head title="POS appearance" />
      <Err error={loadErr} />
      {!bundle && !loadErr && <div className="loading">Loading…</div>}
      {bundle && (
        <div className="set-cols">
          <PosUiConfig base={base} />
          <div className="set-stack">
            <Receipt base={base} initial={bundle.receipt} />
            <OrderAlerts base={base} initial={bundle.orderAlerts} />
            <UpdateChannel base={base} initial={bundle.posUpdateChannel} />
          </div>
        </div>
      )}
    </>
  );
}

function PosUiConfig(props: { base: string }) {
  const { base } = props;
  const [text, setText] = useState<string | null>(null);
  const act = useAction();
  const show = (v: unknown) => setText(v === null ? '' : JSON.stringify(v, null, 2));

  useEffect(() => {
    api.get<unknown>(`${base}/pos-ui-config`).then(show, (e) => act.setError(errMsg(e)));
  }, [base, act.setError]);

  const save = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text?.trim() || '{}');
    } catch {
      act.setError('That is not valid JSON — fix it before saving.');
      return;
    }
    void act.run(async () => show(await api.put<unknown>(`${base}/pos-ui-config`, parsed)));
  };

  return (
    <div className="panel" data-section="pos-ui">
      <h2>POS layout</h2>
      <div className="hint" style={{ margin: '6px 0 10px' }}>
        The tile layout document the terminals read at boot, as JSON.
      </div>
      {text === null ? <div className="loading">Loading…</div> : (
        <textarea className="input mono-area" style={{ minHeight: 380, width: '100%' }} aria-label="POS UI config" value={text}
          placeholder={'{ "tiles": [], "version": 1 }'} onChange={(e) => setText(e.target.value)} />
      )}
      <Err error={act.error} />
      <div style={{ marginTop: 12 }}>
        <SaveBtn busy={act.busy} saved={act.saved} onClick={save} label="Save layout" disabled={text === null} />
      </div>
    </div>
  );
}

function Receipt(props: { base: string; initial: ReceiptSettings }) {
  const { base } = props;
  const [r, setR] = useState(props.initial);
  const act = useAction();
  return (
    <div className="panel" data-section="receipt">
      <h2>Receipt</h2>
      <div className="set-stack" style={{ paddingTop: 8, gap: 12 }}>
        <div className="field">
          <label>Header text</label>
          <input className="input" aria-label="Receipt header" placeholder="Thanks for dining with us" value={r.headerText ?? ''}
            onChange={(e) => setR({ ...r, headerText: e.target.value || null })} />
        </div>
        <div className="field">
          <label>Footer text</label>
          <input className="input" aria-label="Receipt footer" placeholder="See you soon" value={r.footerText ?? ''}
            onChange={(e) => setR({ ...r, footerText: e.target.value || null })} />
        </div>
        <div>
          <Check label="Print the store logo" checked={r.showStoreLogo} onChange={(v) => setR({ ...r, showStoreLogo: v })} />
          <Check label="Print customer details" checked={r.showCustomerInfo} onChange={(v) => setR({ ...r, showCustomerInfo: v })} />
          <Check label="Print the table" checked={r.showTableInfo} onChange={(v) => setR({ ...r, showTableInfo: v })} />
          <Check label="Print the GST breakdown" checked={r.showGstBreakdown} onChange={(v) => setR({ ...r, showGstBreakdown: v })} />
        </div>
        <Err error={act.error} />
        <div>
          <SaveBtn busy={act.busy} saved={act.saved} label="Save receipt"
            onClick={() => void act.run(async () => {
              setR(await api.put<ReceiptSettings>(`${base}/settings/receipt`, { ...r, headerText: r.headerText?.trim() || null, footerText: r.footerText?.trim() || null }));
            })} />
        </div>
      </div>
    </div>
  );
}

function OrderAlerts(props: { base: string; initial: OrderAlertSettings }) {
  const { base } = props;
  const [oa, setOa] = useState(props.initial);
  const act = useAction();
  return (
    <div className="panel" data-section="order-alerts">
      <h2>New-order alerts</h2>
      <div className="set-stack" style={{ paddingTop: 8, gap: 12 }}>
        <div>
          <Check label="Ring for new orders" checked={oa.ringNewOrders} onChange={(v) => setOa({ ...oa, ringNewOrders: v })} />
          <Check label="Repeat until handled" checked={oa.repeatAlert} onChange={(v) => setOa({ ...oa, repeatAlert: v })} />
        </div>
        <div className="frow">
          <div className="field narrow" style={{ flexBasis: 150, minWidth: 150 }}>
            <label>Repeat every (sec)</label>
            <input className="input" type="number" min={0} aria-label="Repeat every (sec)" value={oa.repeatIntervalSeconds ?? ''}
              onChange={(e) => setOa({ ...oa, repeatIntervalSeconds: intOrNull(e.target.value) })} />
          </div>
          <div className="field narrow" style={{ flexBasis: 170, minWidth: 170 }}>
            <label>Unhandled after (min)</label>
            <input className="input" type="number" min={0} aria-label="Unhandled after (min)" value={oa.unprocessedAlertMinutes ?? ''}
              onChange={(e) => setOa({ ...oa, unprocessedAlertMinutes: intOrNull(e.target.value) })} />
          </div>
          <div className="field">
            <label>Alert email (optional)</label>
            <input className="input" type="email" aria-label="Alert email" value={oa.alertEmail ?? ''}
              onChange={(e) => setOa({ ...oa, alertEmail: e.target.value.trim() || null })} />
          </div>
        </div>
        <Err error={act.error} />
        <div>
          <SaveBtn busy={act.busy} saved={act.saved} label="Save alerts"
            onClick={() => void act.run(async () => { setOa(await api.put<OrderAlertSettings>(`${base}/settings/order-alerts`, oa)); })} />
        </div>
      </div>
    </div>
  );
}

function UpdateChannel(props: { base: string; initial: PosUpdateChannel }) {
  const { base } = props;
  const [channel, setChannel] = useState(props.initial);
  const act = useAction();
  return (
    <div className="panel" data-section="update-channel" data-channel={channel}>
      <h2>POS updates</h2>
      <div className="frow" style={{ paddingTop: 8 }}>
        <div className="field">
          <label>Release rail</label>
          <select className="input" aria-label="Update channel" value={channel} onChange={(e) => setChannel(e.target.value as PosUpdateChannel)}>
            <option value="stable">Stable — the promoted release</option>
            <option value="beta">Beta — every new release first</option>
          </select>
        </div>
        <SaveBtn busy={act.busy} saved={act.saved} label="Save rail"
          onClick={() => void act.run(async () => {
            setChannel((await api.put<{ channel: PosUpdateChannel }>(`${base}/settings/pos-update-channel`, { channel })).channel);
          })} />
      </div>
      <Err error={act.error} />
    </div>
  );
}
